'use client';

import { Box, Button, HStack, NativeSelect, Text, VStack } from '@chakra-ui/react';
import { ExternalLink, MessageSquare, Unplug } from 'lucide-react';
import { useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/app/auth-provider';
import { useProject } from '@/contexts/ProjectContext';

interface SlackInstallationSummary {
  id: string;
  enterpriseName?: string | null;
  teamName?: string | null;
  status: 'active' | 'revoked' | 'needs_reconnect';
  updatedAt: string;
}

export function SlackAgentIntegration({
  canManage,
  onStatusChanged,
}: {
  canManage: boolean;
  onStatusChanged?: () => void;
}) {
  const { user } = useAuth();
  const { currentProject, manageableWorkspaces } = useProject();
  const searchParams = useSearchParams();
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string | null>(null);
  const requestedWorkspaceId = searchParams.get('workspace_id');
  const workspaceId =
    selectedWorkspaceId ||
    requestedWorkspaceId ||
    currentProject?.workspace_id ||
    manageableWorkspaces[0]?.id ||
    null;
  const mayManage =
    manageableWorkspaces.some(item => item.id === workspaceId) ||
    (currentProject?.workspace_id === workspaceId && canManage);
  const callbackStatus = searchParams.get('slack');
  const loadSequence = useRef(0);
  const [installations, setInstallations] = useState<SlackInstallationSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);

  const load = useCallback(async() => {
    const sequence = ++loadSequence.current;
    setInstallations([]);
    setNotificationsEnabled(false);
    setError(null);
    if (!user || !workspaceId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const token = await user.getIdToken();
      const response = await fetch(
        `/api/integrations/slack/status?workspaceId=${encodeURIComponent(workspaceId)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const payload = (await response.json()) as {
        installations?: SlackInstallationSummary[];
        notificationsEnabled?: boolean;
        error?: string;
      };
      if (sequence !== loadSequence.current) return;
      if (!response.ok) throw new Error(payload.error || 'Unable to load Slack status');
      setInstallations(payload.installations || []);
      setNotificationsEnabled(payload.notificationsEnabled === true);
    } catch (loadError) {
      if (sequence !== loadSequence.current) return;
      setError(loadError instanceof Error ? loadError.message : 'Unable to load Slack status');
    } finally {
      if (sequence === loadSequence.current) setLoading(false);
    }
  }, [user, workspaceId]);

  useEffect(() => {
    setBusy(false);
    void load();
    return () => {
      loadSequence.current += 1;
    };
  }, [load]);

  const connect = async() => {
    if (!user || !workspaceId || !mayManage || busy) return;
    setBusy(true);
    setError(null);
    try {
      const token = await user.getIdToken();
      const response = await fetch('/api/integrations/slack/install', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ workspaceId }),
      });
      const payload = (await response.json()) as { authorizeUrl?: string; error?: string };
      if (!response.ok || !payload.authorizeUrl) {
        throw new Error(payload.error || 'Unable to start Slack installation');
      }
      window.location.assign(payload.authorizeUrl);
    } catch (connectError) {
      setError(connectError instanceof Error ? connectError.message : 'Unable to connect Slack');
      setBusy(false);
    }
  };

  const toggleNotifications = async() => {
    if (!user || !workspaceId || busy) return;
    const sequence = loadSequence.current;
    setBusy(true);
    setError(null);
    try {
      const token = await user.getIdToken();
      const response = await fetch('/api/integrations/slack/status', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ workspaceId, notificationsEnabled: !notificationsEnabled }),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || 'Unable to save notification preference');
      if (sequence === loadSequence.current)
        setNotificationsEnabled(payload.notificationsEnabled === true);
    } catch (notificationError) {
      if (sequence === loadSequence.current)
        setError(
          notificationError instanceof Error
            ? notificationError.message
            : 'Unable to save notification preference',
        );
    } finally {
      if (sequence === loadSequence.current) setBusy(false);
    }
  };

  const disconnect = async(installationId: string) => {
    if (!user || !workspaceId || !mayManage || busy) return;
    setBusy(true);
    setError(null);
    try {
      const token = await user.getIdToken();
      const response = await fetch('/api/integrations/slack/disconnect', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ workspaceId, installationId }),
      });
      const payload = (await response.json().catch(() => ({}))) as { error?: string };
      if (!response.ok) throw new Error(payload.error || 'Unable to disconnect Slack');
      await load();
      onStatusChanged?.();
    } catch (disconnectError) {
      setError(
        disconnectError instanceof Error ? disconnectError.message : 'Unable to disconnect Slack',
      );
    } finally {
      setBusy(false);
    }
  };

  if (!workspaceId) {
    return (
      <Text color="ui.text.secondary">
        Join an Attune workspace to connect Slack. A project is not required.
      </Text>
    );
  }

  return (
    <VStack align="stretch" gap="4">
      <Box>
        <HStack gap="2">
          <MessageSquare size={18} />
          <Text fontWeight="700">Attune Agent for Slack</Text>
        </HStack>
        <Text mt="2" fontSize="sm" color="ui.text.secondary">
          Use Attune from DMs, channel mentions, threads, and /attune. Responses stream natively,
          with progress and file delivery. Research spans accessible projects in the connected
          workspace. Linked channel participants can continue here; DMs remain private.
        </Text>
      </Box>

      {manageableWorkspaces.length > 0 && (
        <NativeSelect.Root disabled={busy}>
          <NativeSelect.Field
            aria-label="Attune workspace for Slack"
            value={workspaceId}
            onChange={event => setSelectedWorkspaceId(event.target.value)}
          >
            {!manageableWorkspaces.some(item => item.id === workspaceId) && (
              <option value={workspaceId}>Current workspace</option>
            )}
            {manageableWorkspaces.map(item => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>
      )}
      {callbackStatus && (
        <Text
          role="status"
          fontSize="sm"
          color={callbackStatus.startsWith('error:') ? 'status.danger.fg' : 'ui.text.secondary'}
        >
          {slackConnectionOutcome(callbackStatus)}
        </Text>
      )}

      {loading ? <Text fontSize="sm">Checking Slack…</Text> : null}
      {!loading && installations.some(item => item.status === 'active') && (
        <Box>
          <Button
            size="sm"
            variant="outline"
            role="switch"
            aria-checked={notificationsEnabled}
            disabled={busy}
            onClick={() => void toggleNotifications()}
          >
            Report notifications: {notificationsEnabled ? 'On' : 'Off'}
          </Button>
          <Text mt="1" fontSize="xs" color="ui.text.secondary">
            Personal preference for all your linked workspaces. Sends authorized report-completion
            DMs after you link your Slack account.
          </Text>
        </Box>
      )}
      {installations.map(installation => (
        <HStack
          key={installation.id}
          justify="space-between"
          borderWidth="1px"
          borderColor="ui.border.subtle"
          borderRadius="glassInput"
          p="3"
        >
          <Box>
            <Text fontWeight="650">
              {installation.enterpriseName || installation.teamName || installation.id}
            </Text>
            <Text fontSize="xs" color="ui.text.secondary">
              {installation.status === 'active' ? 'Connected' : 'Reconnect required'}
            </Text>
          </Box>
          {mayManage ? (
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => void disconnect(installation.id)}
            >
              <Unplug size={14} /> Disconnect
            </Button>
          ) : null}
        </HStack>
      ))}

      {error ? (
        <Text color="status.danger.fg" fontSize="sm">
          {error}
        </Text>
      ) : null}
      {mayManage ? (
        <Button alignSelf="flex-start" disabled={busy} onClick={() => void connect()}>
          <ExternalLink size={14} />
          {installations.some(item => item.status === 'active')
            ? 'Reconnect Slack'
            : 'Add to Slack'}
        </Button>
      ) : (
        <Text fontSize="sm" color="ui.text.secondary">
          A workspace owner or admin manages this connection.
        </Text>
      )}
    </VStack>
  );
}

function slackConnectionOutcome(status: string): string {
  if (status === 'connected')
    return 'Slack is connected. Open Attune in Slack and send a message to link your account.';
  if (status === 'linked')
    return 'Your account is linked. Return to Slack and continue your conversation.';
  const error = status.replace(/^error:/, '');
  const messages: Record<string, string> = {
    access_denied:
      'Slack authorization was cancelled. No connection was changed; try again when ready.',
    wrong_attune_account:
      'Sign in to the Attune account that started this connection, then open the link again.',
    wrong_slack_account:
      'Use the Slack account that requested the link. Request a fresh link in Slack.',
    wrong_slack_workspace:
      'Choose the Slack workspace connected to Attune, not another workspace in your organization. Request a fresh link there.',
    account_already_linked:
      'This Slack identity is linked to a different Attune account. Sign in to that account or contact your workspace admin.',
    attune_account_already_linked:
      'Your Attune account is already linked to another Slack account in this organization. Use that Slack account; contact your workspace admin to change the link.',
    state_used:
      'This authorization link was already used. Request a fresh link in Slack or reconnect here.',
    link_expired:
      'This one-use link expired. Send Attune a message in Slack to request a new link.',
    workspace_access:
      'Your Attune account cannot access this workspace. Ask its admin to grant membership.',
    workspace_already_connected:
      'Slack is connected to another Attune workspace. Disconnect it there, then reconnect in this workspace.',
    installation_inactive:
      'The Slack connection was revoked or needs reconnecting. Ask a workspace admin to reconnect it.',
    insufficient_user_scopes:
      'Required Slack user permissions were not granted. Reconnect and approve the requested permissions.',
    insufficient_bot_scopes:
      'The Slack app is missing required permissions. Ask a workspace admin to reconnect with the current app configuration.',
  };
  return (
    messages[error] ||
    'Slack authorization could not be completed. Request a new link in Slack or reconnect here.'
  );
}
